// Modal UI for global search and command execution.
import { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, InputBase, Box, Typography } from '@mui/material';
import GoBarModalResultsList from './GoBarModalResultsList';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/i18n/useT';
import useGoBarSearch from './useGoBarSearch.js';
import { useGoBar } from './useGoBar';
import { useFilterContext } from '@/features/tasks/hooks/list/use-list-filters.js';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';
import {
  parseCommand,
  buildGoBarDisplayItems,
} from '@/features/search/goBarHelpers';
import { executeGoBarItem } from '@/features/search/goBarExecute';
import {
  isEditableFocused,
  isGoBarOpenShortcut,
} from '@/constants/keyboardShortcuts';

const GoBarModal = () => {
  const t = useT();
  const { isOpen, openGoBar, closeGoBar } = useGoBar();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { searchTerm, setSearchTerm, results } = useGoBarSearch();
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { setHiddenProjects, setHiddenContexts } = useFilterContext();
  const {
    projectDisplayMap,
    contextDisplayMap,
    projectFilter,
    contextFilter,
    setProjectFilter,
    setContextFilter,
  } = useTasksContext();
  const { addTask } = useAddTask();

  const command = useMemo(() => parseCommand(searchTerm.trim()), [searchTerm]);
  const displayItems = useMemo(
    () => buildGoBarDisplayItems({ command, results, searchTerm, t }),
    [command, results, searchTerm, t],
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (isGoBarOpenShortcut(e)) {
        if (isOpen && document.activeElement === inputRef.current) return;
        if (!isOpen && isEditableFocused()) return;

        e.preventDefault();
        openGoBar();
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [openGoBar, isOpen]);

  const handleClose = () => {
    closeGoBar();
    setSearchTerm('');
    setSelectedIndex(0);
  };

  const executeItem = (item) =>
    executeGoBarItem({
      item,
      searchTerm,
      command,
      addTask,
      setProjectFilter,
      setContextFilter,
      setHiddenProjects,
      setHiddenContexts,
      projectDisplayMap,
      contextDisplayMap,
      projectFilter,
      contextFilter,
      navigate,
      handleClose,
    });

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < displayItems.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = displayItems[selectedIndex];
      if (item) executeItem(item);
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <Dialog
      id="gobar-dialog"
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2, top: '-20vh', position: 'relative' },
      }}
      TransitionProps={{
        onEntered: () => inputRef.current?.focus(),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <InputBase
            inputRef={inputRef}
            fullWidth
            placeholder={t('gobar.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls="gobar-listbox"
            aria-activedescendant={
              displayItems[selectedIndex]
                ? `gobar-option-${displayItems[selectedIndex].id}`
                : undefined
            }
            sx={{
              fontSize: '1.2rem',
              '&.Mui-focused': { borderColor: 'secondary.main' },
            }}
          />

        <GoBarModalResultsList
          displayItems={displayItems}
          selectedIndex={selectedIndex}
          onItemClick={executeItem}
        />
        </Box>
        <Box
          component="footer"
          data-testid="gobar-keyboard-hint"
          aria-hidden="true"
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" component="p">
            {t('gobar.keyboardHint')}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default GoBarModal;
